import { ArrowLeft, Copyright, FileText } from "lucide-react";
import { Link } from "wouter";

type LegalPageKind = "terms" | "copyright";

const legalContent: Record<LegalPageKind, { title: string; description: string; icon: typeof FileText }> = {
  terms: {
    title: "Terms of Use",
    description: "The terms that govern access to and use of the Track Labs platform.",
    icon: FileText,
  },
  copyright: {
    title: "Copyright",
    description: "Ownership and permitted use of Track Labs content, software, and materials.",
    icon: Copyright,
  },
};

function TermsOfUseContent() {
  return (
    <>
      <section>
        <h2>1. Acceptance of these terms</h2>
        <p>By accessing or using Track Labs, you agree to these Terms of Use. If you use Track Labs on behalf of an organization, you represent that you are authorized to accept these terms for that organization.</p>
      </section>
      <section>
        <h2>2. The Track Labs service</h2>
        <p>Track Labs provides organization management tools that may include scheduling, attendance, workflow, reporting, training, and related operational features. Features may vary by workspace, subscription, role, or configuration.</p>
      </section>
      <section>
        <h2>3. Accounts and access</h2>
        <p>You are responsible for keeping your sign-in credentials confidential and for activity performed through your account. You must provide accurate information, use only the workspaces and features you are authorized to access, and promptly report suspected unauthorized access.</p>
      </section>
      <section>
        <h2>4. Acceptable use</h2>
        <p>You may not misuse the service, interfere with its operation, attempt to bypass access controls, introduce malicious code, reverse engineer the platform except where permitted by law, or use Track Labs to violate applicable law or another person’s rights.</p>
      </section>
      <section>
        <h2>5. Customer content and responsibility</h2>
        <p>Your organization remains responsible for the information it enters, imports, stores, or shares through Track Labs, including ensuring that it has the necessary rights and notices for that information. You are also responsible for reviewing records and decisions produced using the platform before taking operational, employment, or other consequential action.</p>
      </section>
      <section>
        <h2>6. Intellectual property</h2>
        <p>Track Labs and its licensors retain all rights in the platform, software, designs, trademarks, documentation, and other Track Labs materials except for rights expressly granted to you. These terms do not transfer ownership of Track Labs intellectual property.</p>
      </section>
      <section>
        <h2>7. Availability and changes</h2>
        <p>We may update, improve, suspend, or discontinue features from time to time. We will make reasonable efforts to keep the service available, but uninterrupted availability is not guaranteed. We may update these terms when the service or applicable requirements change.</p>
      </section>
      <section>
        <h2>8. Disclaimers and limitation</h2>
        <p>Track Labs is provided on an “as available” basis. To the extent permitted by law, Track Labs disclaims warranties not expressly stated in a written agreement. Nothing in these terms excludes rights or remedies that cannot legally be excluded.</p>
      </section>
      <section>
        <h2>9. Contact</h2>
        <p>For questions about these terms or your organization’s use of Track Labs, contact your organization administrator or the Track Labs support contact provided for your workspace.</p>
      </section>
    </>
  );
}

function CopyrightContent() {
  return (
    <>
      <section>
        <h2>Copyright notice</h2>
        <p>© 2026 Track Labs. All rights reserved.</p>
        <p>Track Labs, the Track Labs name and logo, and related product names, designs, interfaces, documentation, graphics, and software are owned by Track Labs or its licensors and are protected by applicable intellectual-property laws.</p>
      </section>
      <section>
        <h2>Permitted use</h2>
        <p>Authorized users may access and use Track Labs materials solely as part of their permitted use of the Track Labs service. You may not copy, republish, distribute, sell, modify, frame, scrape, or create derivative works from Track Labs materials without prior written permission, except where applicable law expressly permits it.</p>
      </section>
      <section>
        <h2>Organization content</h2>
        <p>Content uploaded or entered by a customer organization remains subject to that organization’s rights and responsibilities. Customer content does not become Track Labs content merely because it is stored or displayed through the service.</p>
      </section>
      <section>
        <h2>Third-party materials</h2>
        <p>Some names, trademarks, or materials displayed through Track Labs may belong to third parties. Their appearance does not imply sponsorship or ownership by Track Labs. Third-party rights remain with their respective owners.</p>
      </section>
      <section>
        <h2>Copyright concerns</h2>
        <p>If you believe material available through Track Labs infringes your copyright, provide a clear description of the work, the material at issue, your contact information, and a statement explaining the concern through the support contact associated with the relevant workspace.</p>
      </section>
    </>
  );
}

export function LegalPage({ kind }: { kind: LegalPageKind }) {
  const content = legalContent[kind];
  const Icon = content.icon;

  return (
    <main className="track-labs-landing track-labs-legal min-h-screen bg-[#f7f9fc] px-4 py-8 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <header className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <Link href="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-slate-950">
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
          <span className="text-sm font-semibold tracking-[0.14em] text-slate-500">TRACK LABS</span>
        </header>

        <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-gradient-to-br from-cyan-50 via-white to-violet-50 px-6 py-10 sm:px-10">
            <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-white">
              <Icon className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">{content.title}</h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">{content.description}</p>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Effective November 1, 2026</p>
          </div>

          <div className="space-y-8 px-6 py-8 text-[15px] leading-7 text-slate-700 sm:px-10 sm:py-10">
            {kind === "terms" ? <TermsOfUseContent /> : <CopyrightContent />}
          </div>
        </article>

        <footer className="mt-8 text-center text-xs font-medium text-slate-500">
          Track Labs © 2026
        </footer>
      </div>
    </main>
  );
}

export function TermsOfUsePage() {
  return <LegalPage kind="terms" />;
}

export function CopyrightPage() {
  return <LegalPage kind="copyright" />;
}
